import { useState } from 'react';
import clsx from 'classnames';
import { mixpanelTrack } from '@providers/Mixpanel';
import FilePdf from '@components/Icons/FilePdf';
import { getUrlBase } from '@helpers/urlsHelper';
import ProgressIndicator from '@/components/ProgressIndicator';
import { useToast, useUser } from '@/hooks';
import { logError } from '@/providers/ErrorTracking';
import simpleDebug from '@/helpers/simpleDebug';
import s from './DownloadArchive.module.scss';
import InvalidDocumentModal from '../InvalidDocumentModal';

type Props = {
  accessToken?: string;
  className?: string;
  userWorkflow: { id: string };
  documents: { id: string }[];
};
const DownloadPdf: React.FC<Props> = ({ accessToken, className, documents, userWorkflow }) => {
  const [user] = useUser();
  const authToken = user?.token;
  const addToast = useToast();
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [isInvalidDocModalOpen, setInvalidDocModalOpen] = useState(false);
  const [encryptedDocuments, setEncryptedDocuments] = useState([]);

  if (documents.length === 0) {
    return null;
  }

  const pdfDownloadLink = getUrlBase('/api-proxy/workflows/user-workflows/[userWorkflowId]/compiled-documents.pdf', {
    accessToken,
    userWorkflowId: userWorkflow.id,
  });

  const handlePDFClick = (): void => {
    if (authToken) {
      mixpanelTrack('Workflow - Download archive as PDF', { userWorkflowId: userWorkflow.id });
      setIsPDFLoading(true);
      simpleDebug('downloadArchive', 'pdf start');

      const xhr = new XMLHttpRequest();
      xhr.open('GET', pdfDownloadLink, true);

      xhr.responseType = 'blob';

      xhr.onload = () => {
        simpleDebug('downloadArchive', 'pdf done');

        const allHeaders = xhr.getAllResponseHeaders();
        let encryptedDocumentsArray = [];
        try{
          encryptedDocumentsArray = JSON.parse(allHeaders.match(/x-encrypted-documents: (.*)/)?.[1] || '[]');
        }
        catch(e) {
          logError(e, {
            flow: 'workflows',
            userWorkflowId: userWorkflow.id,
          });
        }
        if (encryptedDocumentsArray.length > 0) {
          setEncryptedDocuments(encryptedDocumentsArray);
          setInvalidDocModalOpen(true);
          return;
        }
        addToast(useToast.ToastTypeVariants.SUCCESS, 'Downloaded files as PDF');
        setIsPDFLoading(false);
      };

      xhr.onerror = (error) => {
        simpleDebug('downloadArchive', 'pdf error', error);
        logError(error, {
          flow: 'workflows',
          userWorkflowId: userWorkflow.id,
        });
        addToast(useToast.ToastTypeVariants.ERROR, 'Failed to download files as PDF');
        setIsPDFLoading(false);
      };

      xhr.setRequestHeader('Authorization', authToken);

      xhr.send();
    }
  };

  return (
    <>
      <a
        className={clsx(s['download-link'], className)}
        download
        href={pdfDownloadLink}
        data-cy="downloadPdf"
        onClick={handlePDFClick}
        style={{ opacity: isPDFLoading ? 0.6 : 1, pointerEvents: isPDFLoading ? 'none' : 'auto' }}
      >
        <FilePdf /> Download all as a PDF
        {isPDFLoading && <ProgressIndicator />}
      </a>
      {
        isInvalidDocModalOpen
      && <InvalidDocumentModal
        isInvalidDocModalOpen={isInvalidDocModalOpen}
        setInvalidDocModalOpen={setInvalidDocModalOpen}
        encryptedDocuments={encryptedDocuments}
      />
      }
    </>
  );
};

export default DownloadPdf;